import React, { useMemo } from 'react';
import { Wallet, TrendingUp, Clock, Wrench, Home } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { useMaintenance } from '../../context/MaintenanceContext';
export function ManGuadagniPage() {
  const {
    jobs
  } = useMaintenance();
  const completati = jobs.filter(j => j.status === 'completata');
  const inCorso = jobs.filter(j => j.status === 'accettata' || j.status === 'in_corso');
  const incassato = completati.reduce((s, j) => s + j.earning, 0);
  const daIncassare = inCorso.reduce((s, j) => s + j.earning, 0);
  const payoutPerTrade = useMemo(() => {
    const map: Record<string, number> = {};
    completati.forEach(j => {
      map[j.trade] = (map[j.trade] || 0) + j.earning;
    });
    return Object.entries(map).map(([trade, total]) => ({
      trade,
      total
    })).sort((a, b) => b.total - a.total);
  }, [completati]);
  const maxPayout = payoutPerTrade[0]?.total ?? 1;
  return <MobileLayout data-id="element-3260">
      <div className="mb-6" data-id="element-3261">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3262">Guadagni</h1>
        <p className="text-sm text-muted-foreground mt-0.5" data-id="element-3263">
          Incassi per intervento, breakdown per tipologia e riepiloghi.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-id="element-3264">
        <Card data-id="element-3265">
          <CardContent data-id="element-3266">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3" data-id="element-3267">
              <Wallet size={18} data-id="element-3268" />
            </div>
            <div className="text-2xl font-bold text-navy" data-id="element-3269">€{incassato}</div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-3270">
              Incassato
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-3271">
          <CardContent data-id="element-3272">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-3" data-id="element-3273">
              <Clock size={18} data-id="element-3274" />
            </div>
            <div className="text-2xl font-bold text-navy" data-id="element-3275">€{daIncassare}</div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-3276">
              Da incassare
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-3277">
          <CardContent data-id="element-3278">
            <div className="w-9 h-9 rounded-lg bg-coral/10 text-coral flex items-center justify-center mb-3" data-id="element-3279">
              <TrendingUp size={18} data-id="element-3280" />
            </div>
            <div className="text-2xl font-bold text-navy" data-id="element-3281">
              €{incassato + daIncassare}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-3282">
              Totale stimato
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout per trade */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-3283">
        <Wrench size={16} className="text-coral" data-id="element-3284" />
        Guadagni per specializzazione
      </h2>
      <Card className="mb-6" data-id="element-3285">
        <CardContent className="space-y-4" data-id="element-3286">
          {payoutPerTrade.length > 0 ? payoutPerTrade.map(p => <div key={p.trade} data-id="element-3287">
                <div className="flex items-center justify-between mb-1.5" data-id="element-3288">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-3289">
                    <span className="w-7 h-7 rounded-full bg-coral/10 flex items-center justify-center text-[10px] font-bold text-coral" data-id="element-3290">
                      {p.trade.substring(0, 2).toUpperCase()}
                    </span>
                    {p.trade}
                  </span>
                  <span className="text-sm font-semibold text-navy" data-id="element-3291">
                    €{p.total}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-id="element-3292">
                  <div className="bg-coral h-2 rounded-full transition-all" style={{
              width: `${Math.round(p.total / maxPayout * 100)}%`
            }} data-id="element-3293" />
                </div>
              </div>) : <p className="text-sm text-muted-foreground text-center py-2" data-id="element-3294">
              Nessun guadagno ancora.
            </p>}
        </CardContent>
      </Card>

      {/* Per intervention */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-3295">
        <Home size={16} className="text-coral" data-id="element-3296" />
        Interventi completati
      </h2>
      <Card data-id="element-3297">
        <CardContent noPadding data-id="element-3298">
          {completati.length > 0 ? <ul className="divide-y divide-border" data-id="element-3299">
              {completati.map((j, idx) => <li key={idx} className="flex items-center justify-between gap-3 px-4 py-3" data-id="element-3300">
                  <div className="min-w-0" data-id="element-3301">
                    <div className="text-sm font-medium text-foreground truncate" data-id="element-3302">
                      {j.title}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5" data-id="element-3303">
                      <Wrench size={11} data-id="element-3304" />
                      {j.trade}
                      <span data-id="element-3305">·</span>
                      <Clock size={11} data-id="element-3306" />
                      {j.date}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-navy flex-shrink-0" data-id="element-3307">
                    €{j.earning}
                  </span>
                </li>)}
            </ul> : <p className="text-sm text-muted-foreground text-center py-6" data-id="element-3308">
              Nessun intervento completato.
            </p>}
        </CardContent>
      </Card>
    </MobileLayout>;
}